"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPartFromDocument = createPartFromDocument;
exports.partVersionFromPublishedDocument = partVersionFromPublishedDocument;
exports.createPartVersionFromDocument = createPartVersionFromDocument;
const document_1 = require("../document");
const constants_1 = require("./constants");
/**
 * Create a new part object from a document. The caller is still responsible to
 * set the document's belongs_to_part_uid to the new part's uid! For a document
 * that already has a part, it will recreate a part object with the same uid as
 * the belongs_to_part_uid.
 *
 * @remarks Note that this simply creates data - it does not perform any i/o.
 */
function createPartFromDocument(document, latestVersion) {
    const { uid: document_uid, belongs_to_part_uid, localToDocumentUid } = document;
    if (localToDocumentUid === document_uid) {
        throw new Error("New parts should not be local to themselves");
    }
    if (!latestVersion || latestVersion === document.uid || latestVersion === document.belongs_to_part_uid) {
        throw new Error("New parts should have a new unguessable latest version");
    }
    if (latestVersion === constants_1.headVersionName) {
        throw new Error("New parts should have a new static part version");
    }
    return createPart(
    // NOTE: belongs_to_part_uid is normally initialized to ""
    belongs_to_part_uid || document.uid, document.owner_uid, document.organization_owner_uid, document.organization_member_permission_type, document.enterprise_owner_uid, document.enterprise_member_permission_type, document.enterprise_owner_handle, document.organization_owner_is_enterprise_default, latestVersion, document.localToDocumentUid);
}
/**
 * Create a brand new part object.
 *
 * @param ownerUid The user uid of the owner of the part
 * @param organizationOwnerUid The organization uid of the organization the part belongs to, if it belongs to one
 * @param organizationMemberPermissionType
 * @param enterpriseOwnerUid
 * @param enterpriseMemberPermissionType
 * @param enterpriseOwnerHandle
 * @param organizationOwnerIsEnterpriseDefault
 * @param latestVersion Optionally specify the latest version.  It will default to HEAD otherwise.
 * @param localToDocumentUid If it's a local part, which document it's local to.
 * @param partUid Re-use the document UID to reify the one-to-one relationship
 * @remarks Note that this simply creates data - it does not perform any i/o.
 */
function createPart(documentUid, ownerUid, organizationOwnerUid, organizationMemberPermissionType, enterpriseOwnerUid, enterpriseMemberPermissionType, enterpriseOwnerHandle, organizationOwnerIsEnterpriseDefault, latestVersion, localToDocumentUid) {
    const part = {
        uid: documentUid,
        created_at: new Date().getTime(),
        updated_at: new Date().getTime(),
        latest_version: latestVersion,
        owner_uid: ownerUid,
        // NOTE: this is redundant with uid, but it's not redundant for old parts
        document_uid: documentUid,
        archived: false,
        copy_count: 0,
        fork_count: 0,
        star_count: 0,
        comment_count: 0,
        comment_likes_count: 0,
        usage_count: 0,
        unique_count: 0,
    };
    /*
     * These properties are copied in like this to avoid putting an undefined value in the object, which
     * would trigger errors when eventually (via arbitraryElementData()) passed to firebase-functions-test's makeDocumentSnapshot()
     *
     * Error: Cannot encode undefined to a Firestore Value. Local testing does not yet support objects of type undefined.
     */
    if (organizationOwnerUid) {
        part.organization_owner_uid = organizationOwnerUid;
    }
    if (organizationMemberPermissionType) {
        part.organization_member_permission_type = organizationMemberPermissionType;
    }
    if (enterpriseOwnerUid) {
        part.enterprise_owner_uid = enterpriseOwnerUid;
    }
    if (enterpriseMemberPermissionType) {
        part.enterprise_member_permission_type = enterpriseMemberPermissionType;
    }
    if (enterpriseOwnerHandle) {
        part.enterprise_owner_handle = enterpriseOwnerHandle;
    }
    if (organizationOwnerIsEnterpriseDefault !== undefined) {
        part.organization_owner_is_enterprise_default = organizationOwnerIsEnterpriseDefault;
    }
    if (localToDocumentUid) {
        part.localToDocumentUid = localToDocumentUid;
    }
    return part;
}
/**
 * Creates a new part version from a published document.
 *
 * @param document The document to be converted. It must have a non-empty belongs_to_part_uid!
 * @param partVersionKey The name of the resultant part version. It should be a standard GUID.
 */
function partVersionFromPublishedDocument(document, partVersionKey) {
    if (document.belongs_to_part_uid === "") {
        throw new Error("unpublished document passed where published document expected in creating part version");
    }
    return createPartVersionFromDocument(document, document.belongs_to_part_uid, partVersionKey);
}
/**
 * Creates a new part version from a document. The provided part uid should
 * match the document's belongs_to_part_uid if it exists.
 *
 * @param document The document to be published.
 * @param partUid The part to be published.
 * @param partVersionKey The name of the resultant part version. It should be a standard GUID, or "HEAD".
 *
 * QUESTION: split this into two functions, one for HEAD and one for non-HEAD?
 */
function createPartVersionFromDocument(document, partUid, partVersionKey) {
    const symbolImage = (0, document_1.symbolResourceFile)(document.assets);
    const previewImage = (0, document_1.previewResourceFile)(document.preview_image, document.assets);
    if (document.belongs_to_part_uid && document.belongs_to_part_uid !== partUid) {
        throw new Error("Document belongs_to_part_uid does not match partUid");
    }
    if (partVersionKey !== constants_1.headVersionName && partVersionKey === document.uid) {
        throw new Error(`New part versions should have a new unique UID different from the document UID: ${partVersionKey}`);
    }
    const partVersion = {
        created_at: Date.now(),
        // Following fields are static once created
        part_uid: partUid,
        owner_uid: document.owner_uid,
        version: partVersionKey,
        document_import_uid: document.uid,
        name: document.name,
        has_custom_name: document.has_custom_name,
        description: document.description,
        behaviour: document.behaviour || "",
        behaviour_has_errors: (0, document_1.documentBehaviorHasErrors)(document),
        controls: document.controls || [],
        is_original: (0, document_1.documentIsOriginal)(document),
        roles: document.roles,
        behaviour_is_derived: (0, document_1.documentBehaviorIsDerived)(document.elements),
        terminals: (0, document_1.getTerminals)(document.elements),
        extracted_element_cache: (0, document_1.getExtractedElementsWithoutExpressionEvaluation)(document.elements),
        assets: document.assets,
        preview_image: previewImage,
        symbol_resource_file: symbolImage,
        symbolLayoutStrategy: document.symbolLayoutStrategy,
        properties: document.properties || {},
        pcbLayoutNodes: (0, document_1.getUnbakedPcbLayoutNodes)(document),
        pcbLayoutRuleSets: document.pcbLayoutRuleSets,
    };
    /*
     * These properties are copied in like this to avoid putting an undefined value in the object, which
     * would trigger errors when eventually (via arbitraryElementData()) passed to firebase-functions-test's makeDocumentSnapshot()
     *
     * Error: Cannot encode undefined to a Firestore Value. Local testing does not yet support objects of type undefined.
     */
    if (document.organization_owner_uid) {
        partVersion.organization_owner_uid = document.organization_owner_uid;
    }
    if (document.organization_member_permission_type !== undefined) {
        partVersion.organization_member_permission_type = document.organization_member_permission_type; // may be zero
    }
    if (document.enterprise_owner_uid) {
        partVersion.enterprise_owner_uid = document.enterprise_owner_uid;
    }
    if (document.enterprise_member_permission_type !== undefined) {
        partVersion.enterprise_member_permission_type = document.enterprise_member_permission_type; // may be zero
    }
    if (document.enterprise_owner_handle) {
        partVersion.enterprise_owner_handle = document.enterprise_owner_handle;
    }
    if (document.organization_owner_is_enterprise_default !== undefined) {
        partVersion.organization_owner_is_enterprise_default = document.organization_owner_is_enterprise_default; // may be false
    }
    if (document.localToDocumentUid) {
        partVersion.localToDocumentUid = document.localToDocumentUid;
    }
    return partVersion;
}
//# sourceMappingURL=lifecycle.js.map